import { ScreenManager, ScreenManagerEvents } from "../../managers/ScreenManager";
import { Showable } from "../../mixins/Showable";
import { ApplyMixins } from "../../utils/ApplyMixins";
import { ScreenOrientationType } from "../../utils/GetOrientation";
import { RectSize } from "../../utils/RectSize";
import { SparkleParticle } from "../animation/SparkleParticle";
import { LoaderFileKeys } from "../constant/AppConstants";
import { OptionsDataManager } from "../managers/OptionsDataManager";
import { LoadingStripe } from "../loading/LoadingStripe";
import { AppLocalization } from "../localization/AppLocalization";
import { AppSettings } from "../managers/AppSettings";
import { AppTimings } from "../data/AppConstants";


// mixins linting
export interface LoadingScreen extends Showable { }

export enum LoadingScreenEvents {
    // FADEIN_COMPLETE = "fadein_complete",
    // LOAD_START = "load_start",
    LOAD_COMPLETE = "load_complete",
    // FADEOUT_COMPLETE = "fadeout_complete",
}

/**
 * @usage
        // loadingScreen
        this.loadingScreen = new LoadingScreen(this);
        this.loadingScreen.Init(this.OnLoadingScreenHideStart.bind(this));
        this.loadingScreen.Build();
        this.loadingScreen.Hide(false);
        this.add.existing(this.loadingScreen);
        // loading
        this.loadingScreen.Show();
        this.loadingScreen.Start();
        this.load.on(Phaser.Loader.Events.PROGRESS, this.OnLoadProgress, this);
 */
export class LoadingScreen
    extends Phaser.GameObjects.Container {
    private static appliedMixins: boolean = false;
    /**
     * tiled background
     * NOTE: not using @see SmartBackground here due to custom structure of LoadingScreen
     */
    public bg: Phaser.GameObjects.TileSprite;
    /**
     * darkening of bg from sides
     */
    public nsBorders: Phaser.GameObjects.NineSlice;
    /**
     * logo, loading stripe and sparkles are within a container
     * - container is repositioned and resized in OnResize
     * - container allows its children to implement custom fade-in/out-s including reposition and scaling transitions without conflicting with screen RESIZE manipulations of said elements (instead RESIZE will manipulate the container)
     * NOTE: keep contentContainer centered on logo; that way you dont need to use additional container for logo and FX to keep them in same spot (FX can not be moved and will always appear at (0,0) -- afaik)
     */
    public contentContainer: Phaser.GameObjects.Container;
    /**
     * centered logo image
     */
    public logo: Phaser.GameObjects.Image;
    /**
     * sparkles above the logo
     */
    public logoFX: Phaser.GameObjects.Particles.ParticleEmitter;
    /**
     * loading stripe under the logo
     */
    public loadingStripe: LoadingStripe;
    /**
     * Perc of total currently loaded 
     * - normalized float: <0,1>
     * NOTE: loading is officially complete, when both @see curLoadedProgress and @see curAnimatedProgress are 1.
     */
    protected curLoadedProgress: number = 0;
    /**
     * Minimum loading duration of loading simulation tween.
     */
    protected minLoadDuration: number = 2000;
    /**
     * Current progress of loading simulation tween.
     * NOTE: loading is officially complete, when both @see curLoadedProgress and @see curAnimatedProgress are 1.
     */
    protected curAnimatedProgress: number = 0;
    /**
     * Refernece to loading simulation tween.
     */
    protected loadTween: Phaser.Tweens.Tween;
    protected onLoadingScreenHideStart: Function;
    /**
     * When true, GameScene knows that it can show LobbyScreen
     * However, when loading here traces 100% eg isLoadComplete=true,
     * that does not mean we can fadein loadingScreen yet (it might not have been created yet!)
     * ^ thus let GameScreen handle the intersect 
     * @see this.IsLoadComplete 
     * @see GameScreen.create
     */
    protected isLoadComplete: boolean = false;
    protected bordersScale: number = 4;
    protected waitTween: Phaser.Tweens.Tween;


    public IsLoadComplete(): boolean {
        return this.isLoadComplete;
    }

    public Init(onLoadingScreenHideStart: Function): void {
        if (!LoadingScreen.appliedMixins) {
            ApplyMixins(LoadingScreen, [Showable]);
            LoadingScreen.appliedMixins = true;
        }

        this.onLoadingScreenHideStart = onLoadingScreenHideStart;
        // this.eventEmitter = new Phaser.Events.EventEmitter();

        this.SetTweenProps(
            // showTweenConfig
            {
                // x: 50,
                onStart: this.OnShowStart.bind(this),
                // onComplete: this.OnShowComplete.bind(this),
            },
            // hideTweenConfig
            {
                onStart: this.OnHideStart.bind(this),
                // onComplete: this.OnHideComplete.bind(this),
            },
            {},
            {}
            // // showTweenTargetProps
            // undefined,
            // // hideTweenTargetProps
            // {
            //     alpha: 0,
            //     // y: ScreenManager.GetWindowRect().height,
            // },
            // // fromShowTweenTargetProps
            // {
            //     alpha: 1,
            //     // y: 0,
            // }
        );

        // cheap way to do a 1-time Hide(false)
        // avoids calling Hide(false) initially
        // - which avoids calling OnHideComplete initially, and only called once hidden. 
        // this.alpha = 0;
    }

    /**
     * - called via scene's init together with this.Init() 
     *   - for this, 
     *     A) scene's pack.files is used to pre-load assets before scene->create, or
     *     B) 'loading scene' is used to preload assets used by this component, so that they are available
     *        in next scene's init, where we this.Build() this loader.
     */
    public Build(): void {
        // tiled bg
        this.bg = this.scene.make.tileSprite(
            {
                width: 32,
                height: 32,
                key: LoaderFileKeys.loading,
                frame: "loading-bg.png",
            },
            false, // addToScene?
        );
        this.bg.setOrigin(0);
        this.add(this.bg);

        // nsBorder
        // original image width: 768, but downscale in TP to 25%;
        // original borderSize: 256, 25%=64 
        const borderSize = 64;
        this.nsBorders = this.scene.make.nineslice({
            x: 0, y: 0,
            key: LoaderFileKeys.loading,
            frame: "loading-borders.png",
            // width: 512, height: 512, // width,height,
            leftWidth: borderSize, rightWidth: borderSize, topHeight: borderSize, bottomHeight: borderSize // left,right,top,bot
        });
        this.nsBorders.setOrigin(0);
        this.nsBorders.setScale(this.bordersScale); // saves on texture space (later setSize to quarter screen size)
        this.add(this.nsBorders);

        // content container
        this.contentContainer = this.scene.make.container({});
        this.add(this.contentContainer);

        // logo
        this.logo = this.scene.make.image({ key: LoaderFileKeys.loading, frame: "loading-logo.png" });
        this.contentContainer.add(this.logo);

        // logo particles
        this.logoFX = this.CreateImageParticles(this.logo);
        this.contentContainer.add(this.logoFX);

        // preloader
        this.loadingStripe = new LoadingStripe(this.scene);
        this.loadingStripe.Init(
            { texture: LoaderFileKeys.loading, frame: "loading-stripe_bg.png" },
            { texture: LoaderFileKeys.loading, frame: "loading-stripe_fill.png" },
            AppLocalization.GetSharedData().loading.loadingText
        );
        this.loadingStripe.Build();
        this.loadingStripe.scale = 1.17;
        this.loadingStripe.SetValue(0);
        this.loadingStripe.StartAnimate();
        this.contentContainer.add(this.loadingStripe);
    }

    /**
     * Calling this starts the 'minimum duration tween', which animates the loading bar completion, while Clamping it to minimum duration of the tween completion.
     */
    public Start(): void {
        this.loadTween = this.scene.tweens.addCounter({
            duration: this.minLoadDuration,
            ease: Phaser.Math.Easing.Quadratic.Out,
            from: 0,
            to: 1,
            onUpdate: (a, b: any, c, d, e) => {
                // const curAnimatedProgress = this.loadTween.totalProgress; // always linear, not what we want
                this.curAnimatedProgress = b.value;//this.loadTween.getValue();
                const curProgress = Math.min(this.curAnimatedProgress, this.curLoadedProgress);
                this.loadingStripe.SetValue(curProgress);
            },
            onComplete: () => {
                if (this.curLoadedProgress == 1) {
                    this.OnLoadComplete();
                }
            },
        });
    }

    public SetLoadedPerc(value: number): void {
        // console.log("SetLoadedPerc", value, this.curAnimatedProgress);

        this.curLoadedProgress = value;

        const curProgress = Math.min(this.curAnimatedProgress, this.curLoadedProgress);
        this.loadingStripe.SetValue(curProgress);

        if (value == 1 && this.curAnimatedProgress == 1) {
            this.OnLoadComplete();
        }
    }

    private OnLoadComplete(): void {
        // console.log("OnLoadComplete");

        // DEBUG LoadingScreen
        // return;

        // wait a bit before fadeOut (slow devices still struggle during transition)
        this.waitTween?.destroy();
        this.waitTween = this.scene.tweens.addCounter({
            duration: 1000,
            onComplete: () => {
                this.Hide();
                this.isLoadComplete = true;
                this.emit(LoadingScreenEvents.LOAD_COMPLETE);
            },
        });
    }

    private CreateImageParticles(target: Phaser.GameObjects.Image): Phaser.GameObjects.Particles.ParticleEmitter {
        const textureScaleFactor = 1;
        var textures = this.scene.textures;
        var origin = target.getTopLeft();
        var logoSource = {
            getRandomPoint: (vec: any) => {
                do {
                    var x = Phaser.Math.Between(0, target.width - 1);
                    var y = Phaser.Math.Between(0, target.height - 1);
                    var pixel = textures.getPixel(x, y, LoaderFileKeys.loading, "loading-logo.png");
                } while (pixel.alpha < 255);

                vec.setTo(
                    x * textureScaleFactor + origin.x,
                    y * textureScaleFactor + origin.y);
                return vec;
            }
        };

        // launch QUANTITY amount of particles each FREQUENCY ms, each particle lives for LIFESPAN ms.
        const particleEmitter = this.scene.add.particles(0, 0, LoaderFileKeys.loading, {
            frame: "loading-fx_sparkle.png",
            lifespan: 3000,
            frequency: 100,
            rotate: { onEmit: () => Math.random() * 360 },
            scaleX: { onEmit: () => 0 },
            scaleY: { onEmit: () => 0 },
            // blendMode: Phaser.BlendModes.ADD, // seems to break with mobilePipeline and 3.60b19
            emitZone: { type: 'random', source: logoSource },
            particleClass: SparkleParticle as any,
            // advance: 7500,
        });
        // particleEmitter.fastForward(7500);
        SparkleParticle.MAX_SCALE = 1.0 * textureScaleFactor;
        SparkleParticle.SCALE_STEP = 0.015 * textureScaleFactor;

        return particleEmitter;
    }

    protected OnShowStart(): void {
        // console.log("OnShowStart");
        this.OnResizeManual();
        this.RegisterListeners();
    }

    protected OnHideStart(): void {
        // console.log("OnHideStart");
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        let duration: number = OptionsDataManager.GetTiming(AppTimings.screenShowDuration);
        this.scene.tweens.add({
            targets: this,
            x: -windowRect.width * 1.2,
            duration: duration,
            ease: AppSettings.ease.screenShowHideEase,
            onComplete: () => this.OnHideComplete(),
        });

        this.UnregisterListeners();
    }

    protected OnHideComplete(): void {
        this.visible = false;
        this.Destroy();
    }

    protected RegisterListeners(): void {
        ScreenManager.GetEventEmitter().on(ScreenManagerEvents.RESIZE, this.OnResize, this);
    }

    protected UnregisterListeners(): void {
        ScreenManager.GetEventEmitter().off(ScreenManagerEvents.RESIZE, this.OnResize, this);
    }

    protected OnResizeManual(): void {
        // process resize
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        this.OnResize(windowRect);
    }

    private OnResize(windowRect: RectSize): void {
        // console.log("OnResize");

        const screenCenterX: number = (0.5 * windowRect.width) | 0;
        const screenCenterY: number = (0.5 * windowRect.height) | 0;
        // const assetScale = ScreenManager.GetAssetScale(0.9, 0.9);
        const assetScaleLandscape = ScreenManager.GetAssetScale(0.9, 1.0); // baseScale, bias
        const assetScalePortrait = ScreenManager.GetAssetScale(1.5, 1.0);

        this.bg.setSize(windowRect.width, windowRect.height);

        // 
        const inverseBordersScale = 1 / this.bordersScale;
        this.nsBorders.setSize(
            Math.ceil(windowRect.width * inverseBordersScale),
            Math.ceil(windowRect.height * inverseBordersScale)
        );

        this.loadingStripe.setPosition(0, this.logo.displayHeight / 2 + 25);

        // Position / scale based on orientation
        const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
        switch (screenOrientationType) {
            case ScreenOrientationType.LANDSCAPE:
                // contentContainer centered on logo and logoFX
                this.contentContainer.scale = assetScaleLandscape;
                this.contentContainer.setPosition(screenCenterX, screenCenterY - 50 * assetScaleLandscape);
                break;
            case ScreenOrientationType.PORTRAIT:
                // contentContainer centered on logo and logoFX
                this.contentContainer.scale = assetScalePortrait;
                this.contentContainer.setPosition(screenCenterX, screenCenterY - 50 * assetScalePortrait);
                break;
        }
    }

    private Destroy(): void {
        // console.log("Destroy");

        // kill listeners
        this.UnregisterListeners();
        this.loadTween?.destroy();

        // kill particle emitters
        this.logoFX.stop(true); // kill?
        this.logoFX.destroy();
        this.logoFX = null;

        // destroy 'loading' texture
        this.scene.textures.get(LoaderFileKeys.loading).destroy();

        // destroy this container
        this.destroy();
    }
}